import { Box, Paper, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { FileDropZone } from "../../../../components/UIToolkit/Inputs/FileDropZone";
import { SystemBanner } from "../../../../components/UIToolkit/molecules/SystemBanner";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import type { OnboardingJob } from "../../../../services/backend.service";
import { ACCEPTED_TYPES } from "../../utils";
import { WeekParityConfirmDialog } from "../../WeekParityConfirmDialog";
import { LoadOrderCaption } from "../LoadOrderCaption";
import { DataLoadFileRow } from "./DataLoadFileRow";
import { DataLoadResultCard } from "./DataLoadResultCard";
import { DataLoadSharedControls } from "./DataLoadSharedControls";
import { isPending } from "./dataLoadOrder";
import { useDataLoadSection } from "./useDataLoadSection";

/**
 * One run, many files, many kinds. Replaces the old Transform / Create Nurses / Load tabs, which were
 * the same job cut three ways.
 */
type Props = {
	restoredJob?: OnboardingJob;
	onJobCreated?: (job: OnboardingJob) => void;
};

export function DataLoadSection({ restoredJob, onJobCreated }: Props) {
	const { t } = useTranslation();
	const s = useDataLoadSection({ restoredJob, onJobCreated });

	const runLabel = s.isRecording ? t("onboarding_jobs.uploading") : s.isTransforming ? t("onboarding_jobs.transforming") : t("onboarding_load.run");
	// The same predicate runLoads uses, not a looser one: counting files that have no payload yet made
	// the button offer uploads the chain then silently skipped.
	const uploadableCount = s.orderedFiles.filter(isPending).length;
	const unhandledCount = s.files.length - s.orderedFiles.filter((file) => file.skip || file.loadResult !== null || file.transformError !== null).length;

	return (
		<Paper data-testid="data-load-section" sx={{ p: 3, minHeight: "fit-content", overflow: "visible" }}>
			<Typography variant="h5" sx={{ mb: 1 }}>
				{t("onboarding_load.title")}
			</Typography>
			<Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
				{t("onboarding_load.description")}
			</Typography>
			{s.artifactsUnavailable && (
				<SystemBanner
					tone="warning"
					title={t("onboarding_jobs.expired_title")}
					description={t("onboarding_jobs.expired_description")}
					action={
						<SystemButton buttonVariant="secondary" onClick={s.resetAll} data-testid="data-load-expired-rerun">
							{t("onboarding_jobs.expired_action")}
						</SystemButton>
					}
					sx={{ mb: 2 }}
					dataTestId="data-load-expired-banner"
				/>
			)}

			{!s.artifactsUnavailable && (
				<FileDropZone
					multiple
					accept={ACCEPTED_TYPES.join(",")}
					hint={t("onboarding_load.drop_hint")}
					onFiles={s.addFiles}
					onRejected={s.reportRejected}
					dataTestId="data-load-drop-zone"
				/>
			)}

			{!s.artifactsUnavailable && s.files.length > 0 && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
					{s.files.map((file, index) => (
						<DataLoadFileRow
							key={file.id}
							file={file}
							index={index}
							locked={s.locked}
							onKindChange={(kind) => s.setKind(file.id, kind)}
							onModeChange={(mode) => s.setMode(file.id, mode)}
							onRosterActionChange={(action) => s.setRosterAction(file.id, action)}
							onPromptChange={(prompt) => s.setPrompt(file.id, prompt)}
							onTabInstructionChange={(sheet, value) => s.setTabInstruction(file.id, sheet, value)}
							onRemove={() => s.removeFile(file.id)}
						/>
					))}
				</Box>
			)}

			{!s.artifactsUnavailable && (
				<DataLoadSharedControls
					departmentId={s.departmentId}
					showAllowDuplicates={s.hasShiftsOrTemplate}
					allowDuplicates={s.allowDuplicates}
					onAllowDuplicatesChange={s.setAllowDuplicates}
					showMinStartDate={s.hasTemplate}
					minStartDate={s.minStartDate}
					onMinStartDateChange={s.setMinStartDate}
					locked={s.locked}
				/>
			)}

			{!s.artifactsUnavailable && s.unsetKindCount > 0 && (
				<SystemBanner
					title={t("onboarding_load.pick_type", { count: s.unsetKindCount })}
					tone="info"
					sx={{ mb: 2 }}
					dataTestId="data-load-unset-kind-warning"
				/>
			)}

			{!s.artifactsUnavailable && s.hasUndatedDirectShifts && (
				<SystemBanner title={t("onboarding_load.undated_as_is")} tone="warning" sx={{ mb: 2 }} dataTestId="data-load-undated-warning" />
			)}

			{!s.artifactsUnavailable && <LoadOrderCaption entries={s.loadOrderEntries} dataTestId="data-load-order" />}

			{!s.artifactsUnavailable && (
				<Box sx={{ display: "flex", gap: 1, mt: 2 }}>
					<SystemButton
						onClick={() => void s.runTransforms()}
						disabled={!s.canRun}
						loading={s.isRecording || s.isTransforming}
						data-testid="data-load-run-button"
					>
						{runLabel}
					</SystemButton>
					{s.files.length > 0 && (
						<SystemButton buttonVariant="secondary" onClick={s.resetAll} disabled={s.locked} data-testid="data-load-reset-button">
							{t("onboarding_load.reset")}
						</SystemButton>
					)}
				</Box>
			)}

			{!s.artifactsUnavailable && s.phase === "results" && (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 3 }} data-testid="data-load-results">
					{s.orderedFiles.map((file, index) => (
						<DataLoadResultCard
							key={file.id}
							file={file}
							index={index}
							departmentId={s.departmentId}
							timezone={s.chosenDepartment?.timezone}
							isNextInOrder={s.nextToUpload?.id === file.id}
							isLoading={s.isLoading}
							locked={s.locked}
							onRetry={() => void s.retryFile(file.id)}
							isRetrying={s.retryingFileId === file.id}
							onSkipChange={(skip) => s.setSkip(file.id, skip)}
							onFilteredBlobChange={(blob) => s.updateFile(file.id, { filteredBlob: blob })}
							onEarliestDateChange={(date) => s.updateFile(file.id, { earliestDate: date })}
							onUpload={() => void s.uploadOne(file)}
							onRemove={() => s.removeFile(file.id)}
						/>
					))}

					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
						{s.allSettled ? (
							<Typography variant="body2" sx={{ color: "text.secondary" }} data-testid="data-load-all-settled">
								{t("onboarding_load.all_handled")}
							</Typography>
						) : (
							unhandledCount > 0 && (
								<Typography variant="body2" sx={{ color: "text.secondary" }} data-testid="data-load-unhandled">
									{t("onboarding_load.needs_attention", { count: unhandledCount })}
								</Typography>
							)
						)}
						<SystemButton
							onClick={() => void s.uploadAll()}
							disabled={s.isLoading || uploadableCount === 0}
							loading={s.isLoading}
							data-testid="data-load-upload-all-button"
						>
							{uploadableCount === 0 ? t("onboarding_load.all_uploaded") : t("onboarding_load.upload_all", { count: uploadableCount })}
						</SystemButton>
					</Box>
				</Box>
			)}

			{!s.artifactsUnavailable && s.chosenDepartment?.first_sunday && (
				<WeekParityConfirmDialog
					open={s.showWeekParityDialog}
					department={s.chosenDepartment}
					today={DateTime.now().setZone(s.chosenDepartment.timezone)}
					isSwitching={s.isUpdatingFirstSunday}
					onSwitch={s.handleSwitchAndResume}
					onKeepAsIs={s.handleKeepAsIsAndResume}
					onCancel={() => s.setShowWeekParityDialog(false)}
				/>
			)}
		</Paper>
	);
}
